/**
 * Снимок входов генерации — «что лежало в слотах на момент старта».
 *
 * Зачем. Стикер незакрытой генерации возвращает человека в инструмент, и там
 * должны стоять его входы. До 22.09 в снимок уходил ОДИН файл
 * (`sourceImageDbKey`: uploadedImage ?? referenceImages[0] ?? swapImage), а
 * возврат клал его через handleImageUpload — то есть только в uploadedImage
 * или referenceImages, по флагам модели. Видео-тренды («Купидон», «Шторм
 * Сторринг») рисуют фото из третьего стейта — именованных слотов slotImages —
 * и после возврата стояли пустыми, хотя файл в IndexedDB был (владелец 22.09:
 * «у видео теряются фото»). Второе фото, конечный кадр, видео юзера, референсы
 * и аудио не снимались вовсе.
 *
 * Как. Снимок описывает КАЖДЫЙ канал загрузок отдельно и хранится в стикере.
 * Файлы картинок (и видео/аудио до MAX_STORED_MEDIA_BYTES) уходят в ту же
 * IndexedDB, что и первичный исходник истории (`umnik_source_images`), под
 * ключами `<generationId>:<канал>:<индекс>`; серверная ссылка (`url`) едет
 * рядом — в запрос уходит именно она, файл нужен только для превью, которое
 * не протухает вместе с presigned-подписью. Возврат (`restoreInputs` в
 * useGenerateUploads) ставит каждый канал своим сеттером, минуя
 * handleImageUpload и флаги модели — поэтому ему всё равно, успела ли модель
 * карточки доехать до виджета.
 *
 * Ключи, созданные снимком, перечислены в `keys` — их удаляет снятие стикера.
 * Первичный ключ истории (`generationId`) сюда НЕ входит: он живёт с записью
 * в «Моих генерациях» и удаляется вместе с ней.
 */
import type { UploadedFile, UploadedVideo } from '../utils/uploadHelpers'

export interface InputItem {
	/** Ключ файла в IndexedDB (umnik_source_images). Нет — файл не сохранён. */
	dbKey?: string
	/** Серверная ссылка после uploadAndGetUrl — в запрос уходит она. */
	url?: string
	name?: string
	type?: string
	/** Референс «под капотом» из админ-карточки: файл подставляет сервер по
	 *  флагу, ссылка — только для показа. Такое не сохраняем, только помечаем. */
	cardRef?: boolean
	duration?: number
}

export interface InputsSnapshot {
	v: 1
	image?: InputItem | null
	swap?: InputItem | null
	end?: InputItem | null
	/** Обязательное видео модели (Motion Control) — uploadedVideo. */
	video?: InputItem | null
	refVideo?: InputItem | null
	/** Именованные слоты карточки по индексам (null — пустой слот). Когда они
	 *  в игре, refImages не пишем: referenceImages — их зеркало. */
	slots?: (InputItem | null)[]
	refImages?: InputItem[]
	omniRefImages?: InputItem[]
	refVideos?: InputItem[]
	refAudios?: InputItem[]
	videoDuration?: number
	/** Ключи IndexedDB, созданные ЭТИМ снимком — удаляются вместе со стикером. */
	keys: string[]
}

/** Каналы загрузок на момент старта — ровно те стейты, что рендерит UploadsRow. */
export interface InputChannels {
	uploadedImage: UploadedFile | null
	swapImage: UploadedFile | null
	endImage: UploadedFile | null
	uploadedVideo: UploadedVideo | null
	referenceVideo: UploadedVideo | null
	slotImages: (UploadedFile | null)[]
	referenceImages: UploadedFile[]
	omniReferenceImages: UploadedFile[]
	referenceVideos: UploadedVideo[]
	referenceAudios: UploadedFile[]
	selectedVideoDuration: number
}

export interface CaptureDeps {
	/** Запись файла в IndexedDB (saveSourceImage). Инжектируется ради тестов. */
	save: (key: string, file: File) => Promise<void>
	/** Первичный исходник уже сохранён persistSourceImage: тот же File второй
	 *  раз не пишем, ссылаемся на его ключ. */
	primary?: { file: File; key: string }
}

/** Потолок сохраняемого видео/аудио: крупнее — только ссылка. IndexedDB
 *  вместит и больше, но снимок живёт всю вкладку и таких стикеров до четырёх. */
export const MAX_STORED_MEDIA_BYTES = 80 * 1024 * 1024

export function isEmptySnapshot(s: InputsSnapshot): boolean {
	return (
		!s.image &&
		!s.swap &&
		!s.end &&
		!s.video &&
		!s.refVideo &&
		!s.slots?.some(Boolean) &&
		!s.refImages?.length &&
		!s.omniRefImages?.length &&
		!s.refVideos?.length &&
		!s.refAudios?.length
	)
}

/**
 * Снять входы в снимок. Возвращает undefined, когда снимать нечего (текстовая
 * генерация) — стикер тогда живёт без снимка, как раньше.
 */
export async function captureInputs(
	generationId: string,
	ch: InputChannels,
	deps: CaptureDeps,
): Promise<InputsSnapshot | undefined> {
	const keys: string[] = []

	const one = async (
		item: UploadedFile | null | undefined,
		channel: string,
		i = 0,
	): Promise<InputItem | null> => {
		if (!item) return null
		// `file` бывает null: канал восстановлен по ссылке (persisted uploads).
		const file: File | null = item.file ?? null
		const out: InputItem = { url: item.url }
		if (file?.name) out.name = file.name
		if (file?.type) out.type = file.type
		if (item.cardRef) out.cardRef = true
		const duration = (item as UploadedVideo).duration
		if (typeof duration === 'number') out.duration = duration

		const storable = !!file && file.size > 0 && !item.cardRef && file.size <= MAX_STORED_MEDIA_BYTES
		if (storable && file) {
			if (deps.primary && deps.primary.file === file) {
				out.dbKey = deps.primary.key
			} else {
				const key = `${generationId}:${channel}:${i}`
				try {
					await deps.save(key, file)
					out.dbKey = key
					keys.push(key)
				} catch {
					// IndexedDB недоступна — вернёмся по ссылке, превью проживёт час.
				}
			}
		}
		return out.dbKey || out.url ? out : null
	}

	const many = async (list: UploadedFile[], channel: string): Promise<InputItem[]> => {
		const out: InputItem[] = []
		for (const [i, item] of list.entries()) {
			const v = await one(item, channel, i)
			if (v) out.push(v)
		}
		return out
	}

	const snap: InputsSnapshot = { v: 1, keys }
	snap.image = await one(ch.uploadedImage, 'image')
	snap.swap = await one(ch.swapImage, 'swap')
	snap.end = await one(ch.endImage, 'end')
	snap.video = await one(ch.uploadedVideo, 'video')
	snap.refVideo = await one(ch.referenceVideo, 'refVideo')
	if (ch.slotImages.some(Boolean)) {
		const slots: (InputItem | null)[] = []
		for (const [i, item] of ch.slotImages.entries()) slots.push(await one(item, 'slot', i))
		snap.slots = slots
	} else {
		snap.refImages = await many(ch.referenceImages, 'refImage')
	}
	snap.omniRefImages = await many(ch.omniReferenceImages, 'omniRef')
	snap.refVideos = await many(ch.referenceVideos, 'refVideos')
	snap.refAudios = await many(ch.referenceAudios, 'refAudio')
	snap.videoDuration = ch.selectedVideoDuration

	return isEmptySnapshot(snap) ? undefined : snap
}
